import { useEffect, useState } from 'react';
import {
    Typography,
    Button,
    Container,
    TextField,
    Avatar,
    Link,
    CircularProgress,
    Box,
    Paper,
    GlobalStyles
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { usePasswordResetStyles } from '../PasswordResetStyles';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from 'config/ApiService';
import ResetPasswordSuccess from './ResetPasswordSuccess';

export function MadeWithLove() {
    return (
        <Typography variant="body2" color="textSecondary" align="center">
            {'Built with love by the '}
            <Link color="inherit" href="https://eql.ai/">
                EQL
            </Link>
            {' team.'}
        </Typography>
    );
}

function ReturnToLoginLink() {
    return (
        <Typography variant="body2" color="textSecondary" align="center">
            <Link color="inherit" href="/signin">
                Return to Login
            </Link>
        </Typography>
    );
}

const ResetPasswordContainer = () => {
    const { code } = useParams<{ code: string }>();
    const classes = usePasswordResetStyles();
    const { closeSnackbar, enqueueSnackbar } = useSnackbar();

    const [verificationLoading, setVerificationLoading] = useState(true);
    const [isValidCode, setIsValidCode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);

    useEffect(() => {
        if (!code) {
            return;
        }
        verifyPasswordResetCode(auth, code)
            .then(() => setIsValidCode(true))
            .catch(() =>
                enqueueSnackbar('Invalid code, please request a new password reset email', {
                    variant: 'error'
                })
            )
            .finally(() => setVerificationLoading(false));
    }, [code, enqueueSnackbar]);

    if (!code) {
        return (
            <>
                No code found, please{' '}
                <Link href="/forgotPassword">request a new password reset email</Link>.
            </>
        );
    }

    const submitNewPassword = async () => {
        if (password !== confirmPassword) {
            enqueueSnackbar('The passwords must match!', { variant: 'error' });
            return;
        }
        setLoading(true);

        try {
            closeSnackbar('errors');
            await confirmPasswordReset(auth, code, password);
            enqueueSnackbar(
                `Password changed successfully. You may now be redirected to the login page.`,
                {
                    variant: 'success'
                }
            );
            setResetPasswordSuccess(true);
        } catch (e: any) {
            enqueueSnackbar(e.toString(), { variant: 'error', persist: true, key: 'errors' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <GlobalStyles styles={{ body: { backgroundColor: '#fafafa' } }} />
            {verificationLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                    <CircularProgress />
                </Box>
            ) : (
                <Container component="main" maxWidth="md">
                    <Box mt={8} mb={8} mx={1} component={Paper}>
                        <Box className={classes.paper}>
                            {!resetPasswordSuccess ? (
                                <>
                                    <Avatar className={classes.avatar}>
                                        <LockOutlinedIcon />
                                    </Avatar>
                                    <Typography component="h1" variant="h5">
                                        Set Password
                                    </Typography>

                                    {isValidCode ? (
                                        <>
                                            <Typography
                                                variant="body2"
                                                className={classes.passwordInstructions}
                                            >
                                                EQL recommend that you use a machine-generated
                                                password and a password manager to secure your
                                                account. Where this is not possible, we recommend
                                                using Cyber Aware’s 'three random words' approach to
                                                creating a unique password for our platform.
                                                Passwords must be over 8 characters in length,
                                                contain at least one number, at least one special
                                                character and uppercase and lowercase letters.
                                            </Typography>

                                            <form className={classes.form} noValidate>
                                                <Grid container spacing={2}>
                                                    <Grid size={12}>
                                                        <TextField
                                                            variant="outlined"
                                                            required
                                                            fullWidth
                                                            id="password"
                                                            label="Password"
                                                            name="password"
                                                            type="password"
                                                            onChange={(evt) =>
                                                                setPassword(evt.target.value)
                                                            }
                                                            error={password === ''}
                                                        />
                                                    </Grid>
                                                    <Grid size={12}>
                                                        <TextField
                                                            variant="outlined"
                                                            required
                                                            fullWidth
                                                            id="confirmpassword"
                                                            label="Confirm password"
                                                            name="password"
                                                            type="password"
                                                            onChange={(evt) =>
                                                                setConfirmPassword(evt.target.value)
                                                            }
                                                            error={confirmPassword === ''}
                                                        />
                                                    </Grid>
                                                </Grid>
                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={submitNewPassword}
                                                    disabled={loading}
                                                    sx={{ marginTop: 3 }}
                                                >
                                                    Submit
                                                </Button>
                                            </form>
                                        </>
                                    ) : (
                                        <Box mt={2}>
                                            <Typography component="h2" variant="h6">
                                                Sorry, that password reset code is invalid or may
                                                have expired.
                                            </Typography>
                                        </Box>
                                    )}
                                    <Box mt={3}>
                                        <ReturnToLoginLink />
                                    </Box>
                                    <Box mt={5}>
                                        <MadeWithLove />
                                    </Box>
                                </>
                            ) : (
                                <ResetPasswordSuccess />
                            )}
                        </Box>
                    </Box>
                </Container>
            )}
        </>
    );
};

export default ResetPasswordContainer;
